import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import Button from '@/components/button'
import DefaultModal from '@/components/default-modal'
import FormField from '@/components/form-field'
import { Input } from '@/components/input'
import type { AdministradorResumo } from '@/types/administrador'
import styles from './style.module.css'

const schema = yup.object({
  nome: yup.string().trim().required('Preencha o nome'),
  email: yup.string().trim().email('Informe um e-mail válido').required('Preencha o e-mail'),
  telefoneDDI: yup.string().trim().required('Preencha o DDI'),
  telefone: yup.string().trim().required('Preencha o telefone'),
})

export type AdministradorFormValues = yup.InferType<typeof schema>

const valoresVazios: AdministradorFormValues = {
  nome: '',
  email: '',
  telefoneDDI: '55',
  telefone: '',
}

interface AdministradorFormModalProps {
  isOpen: boolean
  toggle: () => void
  administrador: AdministradorResumo | null
  isLoading?: boolean
  onSubmit: (valores: AdministradorFormValues) => void
}

const AdministradorFormModal = ({
  isOpen,
  isLoading = false,
  onSubmit,
  administrador,
  toggle,
}: AdministradorFormModalProps) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<AdministradorFormValues>({
    resolver: yupResolver(schema),
    defaultValues: valoresVazios,
  })

  useEffect(() => {
    if (!isOpen) return

    reset(
      administrador
        ? {
            nome: administrador.nome ?? '',
            email: administrador.email ?? '',
            telefoneDDI: administrador.telefone1DDI ?? '55',
            telefone: administrador.telefone1 ?? '',
          }
        : valoresVazios,
    )
  }, [isOpen, administrador, reset])

  return (
    <DefaultModal
      isOpen={isOpen}
      toggle={toggle}
      title={administrador ? 'Editar administrador' : 'Novo administrador'}
      isLoading={isLoading}
      footer={
        <>
          <Button variant="ghost" onClick={toggle}>
            Cancelar
          </Button>
          <Button
            type="submit"
            form="administrador-form"
            isLoading={isLoading}
            disabled={Boolean(administrador) && !isDirty}
          >
            {administrador ? 'Salvar alterações' : 'Criar administrador'}
          </Button>
        </>
      }
    >
      <form id="administrador-form" className={styles.form} onSubmit={handleSubmit(onSubmit)} noValidate>
        <FormField label="Nome" htmlFor="administrador-nome" required error={errors.nome?.message}>
          <Input
            id="administrador-nome"
            placeholder="Preencha o nome"
            invalid={Boolean(errors.nome)}
            {...register('nome')}
          />
        </FormField>

        <FormField label="Email" htmlFor="administrador-email" required error={errors.email?.message}>
          <Input
            id="administrador-email"
            type="email"
            placeholder="Preencha o e-mail"
            invalid={Boolean(errors.email)}
            {...register('email')}
          />
        </FormField>

        <div className={styles.linha}>
          <FormField label="DDI" htmlFor="administrador-ddi" required error={errors.telefoneDDI?.message}>
            <Input
              id="administrador-ddi"
              placeholder="Preencha o DDI"
              invalid={Boolean(errors.telefoneDDI)}
              {...register('telefoneDDI')}
            />
          </FormField>

          <FormField label="Telefone" htmlFor="administrador-telefone" required error={errors.telefone?.message}>
            <Input
              id="administrador-telefone"
              placeholder="Preencha o telefone"
              invalid={Boolean(errors.telefone)}
              {...register('telefone')}
            />
          </FormField>
        </div>
      </form>
    </DefaultModal>
  )
}

export default AdministradorFormModal
